import { CSSProperties, FC, ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { InfoUserWrapper, StyledToolbar } from './components/Header.styles';
import { HeaderDesktopProps } from './Header.types';
import {
  AllieAvatar } from '../../../components/Media/Avatar';
import { AllieCard } from '../../../components/Card';
import { AllieText } from '../../../components/Text';
import { AllieRadioGroup } from '../../../components/Radio/RadioGroup';
import { AllieItem } from '../../../components/Item/Item';
import { LogoutMobileAppModal } from './components/LogoutModal.mobileApp';
import {
  AllieButton,
  AllieIonRadio,
  AllieLogOutOutlineIcon,
  AllieUserIcon,
  usePlatformDetection,
} from '../../..';
import {
  AllieHeaderMobileAppStyled,
  LogoutButtonMobileAppContainer,
} from './Header.mobileApp.styles';

const HEADER_TITLE = 'header-title-container';
const AVATAR_CONTROL = 'avatar-control-container';

export const AllieHeaderMobileApp: FC<HeaderDesktopProps> = ({
  loggedUser,
  optionsMenuAvatar,
  isAuthenticated,
  showAvatarInfo = true, // Por defecto true para mantener compatibilidad
  menuTitle = 'Menú',
  languages,
  currentLanguage,
  languageLabel,
  onLanguageChange,
}) => {
  const [avatarVisible, setAvatarVisible] = useState(showAvatarInfo ?? true);
  const [isMenuModalOpen, setIsMenuModalOpen] = useState(false);
  const { platforms } = usePlatformDetection();
  const isIos = platforms.includes('ios');

  // Extraer la función de logout y su label del último elemento del array
  const logoutOption = optionsMenuAvatar.at(-1);
  const handleLogout = logoutOption?.onClick;
  const logoutLabel = logoutOption?.label;

  // Escuchar cambios en el contenedor de control de avatar
  useEffect(() => {
    const avatarControlContainer = document.getElementById(AVATAR_CONTROL);
    if (avatarControlContainer) {
      const observer = new MutationObserver(() => {
        const shouldShow = avatarControlContainer.dataset.showAvatar !== 'false';
        setAvatarVisible(shouldShow);
      });

      observer.observe(avatarControlContainer, {
        attributes: true,
        attributeFilter: ['data-show-avatar'],
      });

      return () => observer.disconnect();
    }
    return undefined;
  }, []);

  // Actualizar el estado cuando cambie la prop
  useEffect(() => {
    setAvatarVisible(showAvatarInfo ?? true);
  }, [showAvatarInfo]);

  useLayoutEffect(() => {
    if (!loggedUser || !isAuthenticated || loggedUser.name === 'GUEST') {
      console.log('redirecting to login');
      globalThis.location.href = '/login';
    }
  }, [loggedUser, isAuthenticated]);

  return (
    <>
      <AllieHeaderMobileAppStyled color="neutral-10" isIOS={isIos}>
        <StyledToolbar>
          <div id={HEADER_TITLE} />
          <div id={AVATAR_CONTROL} style={{ display: 'none' }} />
          <InfoUserWrapper slot="end">
            {isAuthenticated && avatarVisible && (
              <AllieButton
                allieType="secondary"
                allieSize="none"
                style={
                  {
                    '--border-width': '0px',
                    padding: 6,
                    backgroundColor: 'var(--allie-color-neutral-20)',
                    borderRadius: 'var(--allie-radius-full)',
                    margin: 0,
                  } as CSSProperties
                }
                onClick={() => {
                  setIsMenuModalOpen(true);
                }}
              >
                <AllieUserIcon slot="icon-only" style={{ fontSize: '24px' }} />
              </AllieButton>
            )}
          </InfoUserWrapper>
        </StyledToolbar>
      </AllieHeaderMobileAppStyled>

      {/* Modal del menú */}

      <LogoutMobileAppModal
        isOpen={isMenuModalOpen}
        title={menuTitle}
        setIsOpen={setIsMenuModalOpen}
      >
        {/* Card con información del usuario */}

        <AllieCard
          style={{
            padding: 0,
            margin: 0,
            borderRadius: 'var(--allie-radius-xxl)',
            boxShadow: 'none',
          }}
          content={{
            style: {
              padding: 0,
            },
            children: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--allie-spacing-lg)',
                  padding: 'var(--allie-spacing-xl)',
                }}
              >
                <AllieAvatar
                  id="avatar-info-id"
                  height={48}
                  width={48}
                  style={{
                    cursor: 'pointer',
                    borderRadius: '100%',
                    border: '1px solid var(--allie-color-neutral-30)',
                    width: 48,
                  }}
                  src={loggedUser?.avatar}
                  name={loggedUser?.name}
                  color="neutral-5"
                  fgColor="neutral-50"
                  textSizeRatio={2.5}
                />
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--allie-spacing-xs)',
                  }}
                >
                  <AllieText type="body1" color="neutral-90" weight={600}>
                    {loggedUser?.name}
                  </AllieText>

                  <AllieText type="body1" color="neutral-60" weight={400}>
                    {loggedUser?.email}
                  </AllieText>
                </div>
              </div>
            ),
          }}
        />

        {/* Language Selection Card */}

        <AllieText type="body1" color="neutral-90" weight={500}>
          {languageLabel}
        </AllieText>
        <AllieCard
          style={{
            padding: 0,
            margin: 0,
            borderRadius: 'var(--allie-radius-xxl)',
            boxShadow: 'none',
          }}
          content={{
            style: {
              padding: 0,
            },
            children: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--allie-spacing-md)',
                }}
              >
                <AllieRadioGroup value={currentLanguage}>
                  {languages.map((lang, index) => (
                    <AllieItem
                      key={lang.code}
                      button
                      onClick={() => onLanguageChange(lang.code)}
                      lines="none"
                      style={
                        {
                          '&::part(native)': {
                            paddingLeft: 'var(--allie-spacing-xl)',
                          },
                          borderBottom:
                            index < languages.length - 1
                              ? '1px solid var(--allie-color-neutral-10)'
                              : 'none',
                        } as CSSProperties
                      }
                    >
                      <AllieText
                        slot="start"
                        type="body1"
                        color="primary-80"
                        style={{ padding: 'var(--allie-spacing-xl) 0' }}
                      >
                        {lang.name}
                      </AllieText>
                      <AllieIonRadio slot="end" value={lang.code} />
                    </AllieItem>
                  ))}
                </AllieRadioGroup>
              </div>
            ),
          }}
        />

        {/* Botón de logout separado */}

        <LogoutButtonMobileAppContainer
          onClick={() => {
            handleLogout?.();
          }}
        >
          <div>
            <AllieButton
              allieType="text"
              allieSize="none"
              onclick={() => {
                handleLogout?.();
              }}
              style={
                {
                  justifyContent: 'flex-start',
                  '--padding-start': 0,
                } as CSSProperties
              }
            >
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--allie-spacing-xs)',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    color: 'var(--allie-color-error-60)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <AllieLogOutOutlineIcon size="small" />
                </div>
                {logoutLabel}
              </div>
            </AllieButton>
          </div>
        </LogoutButtonMobileAppContainer>
      </LogoutMobileAppModal>
    </>
  );
};

export default AllieHeaderMobileApp;

export const AllieHeaderTitle: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const headerContainer = document.getElementById(HEADER_TITLE);

    if (headerContainer) {
      // Clear any previous content in the container
      if (!headerContainer.dataset.mounted) {
        headerContainer.innerHTML = ''; // Clear content
        headerContainer.dataset.mounted = 'true'; // Mark as mounted
        setContainer(headerContainer);
      }
    }

    return () => {
      if (headerContainer) {
        // Cleanup only once
        delete headerContainer.dataset.mounted; // Remove mounted flag
        headerContainer.innerHTML = '';
      }
    };
  }, []); // Run only once

  // If no container exists, render nothing
  if (!container) return null;

  // Portal content to the header container
  return ReactDOM.createPortal(children, container);
};

export const AllieAvatarControl: FC<{
  showAvatar: boolean;
}> = ({ showAvatar }) => {
  useEffect(() => {
    const avatarControlContainer = document.getElementById(AVATAR_CONTROL);
    if (avatarControlContainer) {
      avatarControlContainer.dataset.showAvatar = showAvatar.toString();
    }
  }, [showAvatar]);

  // Este componente no renderiza nada visible
  return null;
};
